/**
 * useAudioDevices
 *
 * Enumerates audio input and output devices, listens for devicechange,
 * and provides a function to request microphone permission so device labels populate.
 */

import { useEffect, useState, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AudioDeviceInfo {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
  groupId: string;
}

// ============================================================================
// Hook
// ============================================================================

const hasMediaDevices = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.enumerateDevices;

export function useAudioDevices(): {
  inputs: AudioDeviceInfo[];
  outputs: AudioDeviceInfo[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
} {
  const [inputs, setInputs] = useState<AudioDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<AudioDeviceInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const enumerate = useCallback(async () => {
    if (!hasMediaDevices) {
      setInputs([]);
      setOutputs([]);
      setError('Media devices API not available');
      setIsLoading(false);
      return;
    }
    setError(null);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inList: AudioDeviceInfo[] = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
          kind: 'audioinput' as const,
          groupId: d.groupId,
        }));
      const outList: AudioDeviceInfo[] = devices
        .filter(d => d.kind === 'audiooutput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`,
          kind: 'audiooutput' as const,
          groupId: d.groupId,
        }));
      setInputs(inList);
      setOutputs(outList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to list devices');
      setInputs([]);
      setOutputs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    enumerate();
  }, [enumerate]);

  useEffect(() => {
    if (!hasMediaDevices) return;
    const onDeviceChange = () => {
      enumerate();
    };
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
  }, [enumerate]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!hasMediaDevices) return false;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach(t => t.stop());
      await enumerate();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone permission denied');
      return false;
    }
  }, [enumerate]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await enumerate();
  }, [enumerate]);

  return {
    inputs,
    outputs,
    isLoading,
    error,
    refresh,
    requestPermission,
  };
}
